import { useCallback, useEffect } from 'react';
import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import type { ServiceState, StateResponse } from '@vinz/shared';
import { apiGet, apiPost, HttpError, UnauthorizedError } from './api-client.js';
import { useAuth } from './auth.js';
import {
  runConsoleAction, type ConsoleAction, type ConsoleOutcome, type ConsolePermissions, type ConsoleRow,
} from './console-actions.js';
import type { HistoryResponse } from './routes/history.js';
import { setSwitch, SWITCH_STATUSES, type SwitchesResponse, type SwitchInfo, type SwitchOutcome } from './switches.js';

// The table changes within seconds of a hold change or a deploy starting or finishing (the worker's
// write plus a one-repo refresh, and the webhook's refreshes), not only at the 5-minute sweep. So the
// page reads it back every 10s, often enough not to be the lag: one DynamoDB-backed read per tab.
const REFETCH_INTERVAL_MS = 10 * 1000;

/** An auth failure is recovered by refreshing the session, never by retrying the same stale token. */
export function apiQueryRetry(failureCount: number, error: Error): boolean {
  return !(error instanceof UnauthorizedError) && failureCount < 3;
}

/**
 * An UnauthorizedError is not a sign-out signal: it means this id token no longer authorizes, which
 * a refresh fixes. The queries carry idToken in their key, so once refreshSession swaps the token in
 * they re-run on their own. Only a failed refresh clears the session.
 */
function useRefreshOnUnauthorized<T>(query: UseQueryResult<T>): UseQueryResult<T> {
  const { refreshSession } = useAuth();
  useEffect(() => {
    if (query.error instanceof UnauthorizedError) void refreshSession();
  }, [query.error, refreshSession]);
  return query;
}

export type SweepReply =
  | { status: 'started'; requestedAt: string }
  | { status: 'cooldown'; lastRequestedAt?: string; retryAfterSeconds: number };

/** POST /sweep. A 429 is the API's one-a-minute cooldown - an answer, not a failure - so it comes back. */
export async function startSweep(idToken: string): Promise<SweepReply> {
  return (await apiPost<SweepReply>('/sweep', idToken, [429])).body;
}

/**
 * startSweep, recovering an expired session the way the queries do: an UnauthorizedError triggers
 * refreshSession, so the next click goes out with a fresh token instead of failing until some other
 * query or a focus event happens to refresh it. The error still reaches the caller to report.
 */
export function useStartSweep(): (idToken: string) => Promise<SweepReply> {
  const { refreshSession } = useAuth();
  return useCallback(async (idToken: string) => {
    try {
      return await startSweep(idToken);
    } catch (err) {
      if (err instanceof UnauthorizedError) void refreshSession();
      throw err;
    }
  }, [refreshSession]);
}

/** While a forced sweep is running the page polls fast, so the new reading shows up as it lands. */
export const SWEEPING_REFETCH_MS = 3 * 1000;

export function useStateQuery(idToken: string | null, opts: { sweeping?: boolean } = {}) {
  return useRefreshOnUnauthorized(useQuery({
    queryKey: ['state', idToken],
    queryFn: () => apiGet<StateResponse>('/state', idToken as string),
    enabled: idToken !== null,
    refetchInterval: opts.sweeping ? SWEEPING_REFETCH_MS : REFETCH_INTERVAL_MS,
    retry: apiQueryRetry,
  }));
}

export interface HistoryQueryParams {
  service?: string;
  from?: string;
}

function historyPath({ service, from }: HistoryQueryParams): string {
  const qs = new URLSearchParams();
  if (service) qs.set('service', service);
  if (from) qs.set('from', from);
  const query = qs.toString();
  return query ? `/history?${query}` : '/history';
}

export function useHistoryQuery(idToken: string | null, params: HistoryQueryParams) {
  return useRefreshOnUnauthorized(useQuery({
    queryKey: ['history', params, idToken],
    queryFn: () => apiGet<HistoryResponse>(historyPath(params), idToken as string),
    enabled: idToken !== null,
    retry: apiQueryRetry,
  }));
}

/**
 * What the viewer may do from the console. Refreshed every minute; the answer is cosmetic (the worker
 * re-checks every action), so while it is loading or failed the page offers no buttons at all.
 */
export const PERMISSIONS_REFRESH_MS = 60 * 1000;

export function usePermissionsQuery(idToken: string | null) {
  return useRefreshOnUnauthorized(useQuery({
    queryKey: ['console-permissions', idToken],
    queryFn: () => apiGet<ConsolePermissions>('/console/permissions', idToken as string),
    enabled: idToken !== null,
    staleTime: PERMISSIONS_REFRESH_MS,
    refetchInterval: PERMISSIONS_REFRESH_MS,
    retry: apiQueryRetry,
  }));
}

/** The API's answers to a console POST - every one is an outcome to show, not a failure to throw. */
const CONSOLE_STATUSES = [202, 400, 401, 403, 404, 409, 502, 504];

/** `requestId` is the caller's: a retry of an unsettled action must send the same one again. */
export type RunConsoleAction = (requestId: string, action: ConsoleAction, svc: ServiceState, durationMs?: number) => Promise<ConsoleOutcome>;

export function useConsoleAction(idToken: string | null): RunConsoleAction {
  const { refreshSession } = useAuth();
  const queryClient = useQueryClient();
  return useCallback(async (requestId, action, svc, durationMs) => {
    if (!idToken) return { ok: false, message: 'Sign in again, then try.', settled: true };
    try {
      const outcome = await runConsoleAction({
        post: (path, body) => apiPost<{ message?: string }>(path, idToken, CONSOLE_STATUSES, body),
        getRow: (requestId) => apiGet<ConsoleRow>(`/console/requests/${requestId}`, idToken),
        sleep: (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
      }, requestId, action, svc, durationMs);
      if (outcome.ok) void queryClient.invalidateQueries({ queryKey: ['state'] });
      return outcome;
    } catch (err) {
      if (err instanceof UnauthorizedError) {
        void refreshSession();
        // The request may already be admitted, so this is unsettled: the retry reuses its id.
        return { ok: false, message: 'Your session had expired — try again.', settled: false };
      }
      // The viewer gets a sentence; devtools keeps the cause.
      console.error('console action failed', err);
      return { ok: false, message: "Couldn't reach Vinz — try again.", settled: false };
    }
  }, [idToken, refreshSession, queryClient]);
}

/** The switches change rarely and only by hand, so a minute is fresh enough; a change made here is applied at once. */
export const SWITCHES_REFRESH_MS = 60 * 1000;

const switchesQuery = (idToken: string | null) => ({
  queryKey: ['switches', idToken],
  queryFn: () => apiGet<SwitchesResponse>('/console/switches', idToken as string),
  retry: apiQueryRetry,
});

export function useSwitchesQuery(idToken: string | null) {
  return useRefreshOnUnauthorized(useQuery({
    ...switchesQuery(idToken),
    enabled: idToken !== null,
    refetchInterval: SWITCHES_REFRESH_MS,
  }));
}

export type SetSwitch = (info: SwitchInfo, value: boolean, expectedVersion: number) => Promise<SwitchOutcome>;

/**
 * POST /console/switches. A success's body is the new state, so it replaces the page's copy at once: the panel only
 * ever shows one state the API returned. Any answer that leaves the page's copy in doubt reads it again.
 */
export function useSetSwitch(idToken: string | null): SetSwitch {
  const { refreshSession } = useAuth();
  const queryClient = useQueryClient();
  return useCallback(async (info, value, expectedVersion) => {
    if (!idToken) return { ok: false, recheck: false, message: 'Sign in again, then try.' };
    let outcome: SwitchOutcome;
    try {
      outcome = await setSwitch({
        post: (body) => apiPost<SwitchesResponse & { ok?: true }>('/console/switches', idToken, SWITCH_STATUSES, body),
      }, info, value, expectedVersion);
    } catch (err) {
      if (err instanceof UnauthorizedError) {
        void refreshSession();
        outcome = { ok: false, recheck: true, message: 'Your session had expired — nothing was changed. Try again.' };
      } else {
        console.error('switch change failed', err);
        outcome = { ok: false, recheck: true, message: err instanceof HttpError
          ? `Vinz answered with an error (HTTP ${err.status}). Showing what it reads now: check before trying again.`
          : "Couldn't reach Vinz. Showing what it reads now: check before trying again." };
      }
    }
    if (outcome.ok) {
      // A read already in flight (the interval, a focus refetch) began before this change and would land after it,
      // putting the old values and version back. Cancel it first; the POST's state is the newer one.
      await queryClient.cancelQueries({ queryKey: ['switches', idToken], exact: true });
      queryClient.setQueryData(['switches', idToken], outcome.state);
    } else if (outcome.recheck) {
      // Read this viewer's switches again now, and know whether that worked: a failed re-read leaves the page unable
      // to say what they are. An expired session is the one exception - the refresh re-reads them with the new token.
      try {
        await queryClient.fetchQuery(switchesQuery(idToken));
      } catch (err) {
        if (!(err instanceof UnauthorizedError)) {
          return { ...outcome, message: "Vinz couldn't be read again afterwards, so it isn't known whether anything changed. Reload before trying again." };
        }
      }
    }
    return outcome;
  }, [idToken, refreshSession, queryClient]);
}
